import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../stores/builderStore';
import { getForm, updateForm, publishForm } from '../lib/api';
import Builder from '../components/builder/Builder';
import { Skeleton, SkeletonFormBuilder } from '../components/ui/Skeleton';
import { useNotification } from '../components/ui/NotificationToast';

export default function FormBuilderPage() {
  const { t } = useTranslation('builder');
  const { id } = useParams<{ id: string }>();
  const { initializeForm, formName, formSchema, hasUnsavedChanges, markAsSaved, reset } = useBuilderStore();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPublishSuccess, setShowPublishSuccess] = useState<{ publicUrl: string; resultsUrl: string } | null>(null);
  const [embedTab, setEmbedTab] = useState<'iframe' | 'js'>('js');

  useEffect(() => {
    if (!id) return;

    async function loadForm() {
      try {
        setLoading(true);
        const form = await getForm(id!);
        initializeForm(form.id, form.name, form.schema);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load form');
      } finally {
        setLoading(false);
      }
    }

    loadForm();

    return () => {
      reset();
    };
  }, [id, initializeForm, reset]);

  async function handleSave() {
    if (!id) return;

    try {
      setSaving(true);
      setError(null);
      await updateForm(id, { schema: formSchema });
      markAsSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save form');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!id) return;

    if (formSchema.fields.length === 0) {
      setError('Add at least one field before publishing');
      return;
    }

    try {
      setPublishing(true);
      setError(null);

      // Save first
      if (hasUnsavedChanges) {
        await updateForm(id, { schema: formSchema });
        markAsSaved();
      }

      const result = await publishForm(id);
      setShowPublishSuccess({ publicUrl: result.publicUrl, resultsUrl: result.resultsUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish form');
    } finally {
      setPublishing(false);
    }
  }

  if (loading) {
    return (
      <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header Skeleton */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="w-6 h-6 rounded" />
            <Skeleton className="h-6 w-48" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-20 rounded-lg" />
            <Skeleton className="h-10 w-24 rounded-lg" />
          </div>
        </header>
        {/* Toolbar Skeleton */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="w-10 h-10 rounded" />
            <Skeleton className="w-10 h-10 rounded" />
            <Skeleton className="w-10 h-10 rounded" />
          </div>
          <div className="flex items-center gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-20 rounded-lg" />
            ))}
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        <SkeletonFormBuilder />
      </div>
    );
  }

  if (error && !formName) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Link to="/formspace" className="btn-primary">
            Back to Forms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/formspace" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{formName}</h1>
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded">
              {t('toolbar.unsavedChanges')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {error && (
            <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className="btn-secondary"
          >
            {saving ? t('toolbar.saving') : t('toolbar.save')}
          </button>

          <button
            onClick={handlePublish}
            disabled={publishing || formSchema.fields.length === 0}
            className="btn-primary"
          >
            {publishing ? t('toolbar.publishing') : t('toolbar.publish')}
          </button>
        </div>
      </header>

      {/* Builder */}
      <Builder formId={id} />

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Form Published!</h2>
              <p className="text-gray-500 dark:text-gray-400">Your form is now live and accepting submissions</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <label className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Public Form URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={window.location.origin + showPublishSuccess.publicUrl}
                    readOnly
                    className="input flex-1 font-mono text-sm"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + showPublishSuccess.publicUrl);
                      showNotification('success', 'URL copied to clipboard!');
                    }}
                    className="btn-secondary"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <label className="text-sm text-blue-600 dark:text-blue-400 block mb-1">Results Viewer URL</label>
                <p className="text-xs text-blue-500 dark:text-blue-400 mb-2">Share this link to allow others to view form submissions</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={window.location.origin + showPublishSuccess.resultsUrl}
                    readOnly
                    className="input flex-1 font-mono text-sm"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + showPublishSuccess.resultsUrl);
                      showNotification('success', 'URL copied to clipboard!');
                    }}
                    className="btn-secondary"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Embed Code Section */}
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-purple-600 dark:text-purple-400 font-medium">Embed on Your Website</label>
                  <div className="flex rounded-lg overflow-hidden border border-purple-200 dark:border-purple-700">
                    <button
                      onClick={() => setEmbedTab('js')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        embedTab === 'js'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50'
                      }`}
                    >
                      JS Widget
                    </button>
                    <button
                      onClick={() => setEmbedTab('iframe')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        embedTab === 'iframe'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50'
                      }`}
                    >
                      iFrame
                    </button>
                  </div>
                </div>
                <p className="text-xs text-purple-500 dark:text-purple-400 mb-3">
                  {embedTab === 'js'
                    ? 'Add this code to embed a responsive form widget'
                    : 'Simple iframe embed for basic integration'}
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">
                    {embedTab === 'js'
                      ? `<!-- Add this where you want the form to appear -->
<div data-myforms-embed="${window.location.origin + showPublishSuccess.publicUrl}" data-height="600"></div>

<!-- Add this before </body> -->
<script src="${window.location.origin}/embed.js"></script>`
                      : `<iframe
  src="${window.location.origin + showPublishSuccess.publicUrl}?embed=true"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;">
</iframe>`}
                  </pre>
                  <button
                    onClick={() => {
                      const code = embedTab === 'js'
                        ? `<!-- Add this where you want the form to appear -->\n<div data-myforms-embed="${window.location.origin + showPublishSuccess.publicUrl}" data-height="600"></div>\n\n<!-- Add this before </body> -->\n<script src="${window.location.origin}/embed.js"></script>`
                        : `<iframe src="${window.location.origin + showPublishSuccess.publicUrl}?embed=true" width="100%" height="600" frameborder="0" style="border: none; border-radius: 8px;"></iframe>`;
                      navigator.clipboard.writeText(code);
                      showNotification('success', 'Embed code copied to clipboard!');
                    }}
                    className="absolute top-2 right-2 px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                  >
                    Copy
                  </button>
                </div>
                {embedTab === 'js' && (
                  <p className="text-xs text-purple-400 dark:text-purple-500 mt-2">
                    Options: <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">data-height</code>, <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">data-theme="dark"</code>, <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">data-hide-header="true"</code>
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPublishSuccess(null)}
                className="btn-secondary flex-1"
              >
                Close
              </button>
              <a
                href={showPublishSuccess.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex-1 text-center"
              >
                View Form
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
